/**
 * References Store - State Management для справочников
 * Аналог lib/features/references/presentation/bloc/references_bloc.dart
 *
 * Загружаются один раз при старте приложения и кешируются
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { referencesApi } from '../api/references.api'
import type {
  LeadSource,
  Country,
  City,
  ArchiveReason,
  ClientTagReference,
} from '../types/references.types'

export const useReferencesStore = defineStore('references', () => {
  // State
  // DEV-42: clientStatuses упразднён вместе со справочником.
  // DEV-232: partners упразднён — атрибуция клиента через clients.agent_id.
  const leadSources = ref<LeadSource[]>([])
  const countries = ref<Country[]>([])
  const cities = ref<City[]>([])
  // DEV-1061/DEV-1062: причины архивации и теги клиента.
  const archiveReasons = ref<ArchiveReason[]>([])
  const clientTags = ref<ClientTagReference[]>([])

  const isLoading = ref(false)
  const isLoaded = ref(false)
  const error = ref<string | null>(null)

  // Getters
  // DEV-42: activeClientStatuses удалён.

  /** Активные источники лидов (отсортированные) */
  const activeLeadSources = computed(() =>
    leadSources.value.filter((source) => source.isActive).sort((a, b) => a.sortOrder - b.sortOrder),
  )

  /** Все страны (для селектов и форм) */
  const allCountries = computed(() => countries.value)

  /** Все города (для селектов и форм) */
  const allCities = computed(() => cities.value)

  // Actions

  /**
   * Загрузить все справочники
   * Вызывается один раз при инициализации приложения
   */
  const loadAll = async () => {
    if (isLoaded.value || isLoading.value) {
      return
    }

    isLoading.value = true
    error.value = null

    try {
      // DEV-42: getClientStatuses удалён из загрузки — справочник упразднён.
      const [sourcesData, countriesData, citiesData, archiveReasonsData, tagsData] =
        await Promise.all([
          referencesApi.getLeadSources(),
          referencesApi.getCountries(),
          referencesApi.getCities(),
          referencesApi.getArchiveReasons(),
          referencesApi.getTags(),
        ])

      leadSources.value = sourcesData
      countries.value = countriesData
      cities.value = citiesData
      archiveReasons.value = archiveReasonsData
      clientTags.value = tagsData

      isLoaded.value = true
    } catch (err: unknown) {
      if (err instanceof Error) {
        error.value = err.message || 'Failed to load references'
      } else {
        error.value = 'Failed to load references'
      }
      console.error('Failed to load references:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Перезагрузить все справочники (force refresh)
   */
  const reload = async () => {
    isLoaded.value = false
    await loadAll()
  }

  // DEV-42: getClientStatusById / getClientStatusByKey удалены.

  /**
   * Получить источник лида по ID
   */
  const getLeadSourceById = (id: string): LeadSource | undefined => {
    return leadSources.value.find((source) => source.id === id)
  }

  /**
   * Получить источник лида по ключу
   */
  const getLeadSourceByKey = (key: string): LeadSource | undefined => {
    return leadSources.value.find((source) => source.key === key)
  }

  /**
   * Получить страну по ID
   */
  const getCountryById = (id: string): Country | undefined => {
    return countries.value.find((country) => country.id === id)
  }

  /**
   * Получить страну по ISO коду
   */
  const getCountryByIsoCode = (isoCode: string): Country | undefined => {
    return countries.value.find(
      (country) => country.isoCode.toLowerCase() === isoCode.toLowerCase(),
    )
  }

  /**
   * Получить город по ID
   */
  const getCityById = (id: string): City | undefined => {
    return cities.value.find((city) => city.id === id)
  }

  /**
   * Получить города по ID страны
   */
  const getCitiesByCountryId = (countryId: string): City[] => {
    return cities.value.filter((city) => city.countryId === countryId)
  }

  /** Причина архивации по ключу — для подписи «нет диалога» и фильтров (DEV-1061). */
  const getArchiveReasonByKey = (key: string): ArchiveReason | undefined =>
    archiveReasons.value.find((reason) => reason.key === key)

  return {
    // State
    leadSources,
    countries,
    cities,
    archiveReasons,
    clientTags,
    isLoading,
    isLoaded,
    error,

    // Getters
    activeLeadSources,
    allCountries,
    allCities,

    // Actions
    loadAll,
    reload,
    getLeadSourceById,
    getLeadSourceByKey,
    getCountryById,
    getCountryByIsoCode,
    getCityById,
    getCitiesByCountryId,
    getArchiveReasonByKey,
  }
})
